/**
 * 应用外壳 —— 侧边栏 + 顶栏 + 内容区。
 *
 * 承担三件容易被漏掉的全局职责：
 * 1. 跳过链接（WCAG 2.4.1）——键盘用户不必每页 Tab 穿过 17 个导航项。
 * 2. 主题持久化到 localStorage，并同步 `data-theme`（design-tokens.css 的切换钩子）。
 * 3. 窄屏抽屉的 Esc 出口与路由跳转后自动关闭 —— 否则抽屉会盖住刚跳过去的页面。
 */
import { useCallback, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { Icon } from '../icons/Icon';
import { findNavItem } from '../app/navigation';
import { ICON_SIZE } from '../tokens';

type Theme = 'light' | 'dark';
const THEME_KEY = 'lh.admin.theme';

function readStoredTheme(): Theme {
  try {
    return globalThis.localStorage?.getItem(THEME_KEY) === 'dark' ? 'dark' : 'light';
  } catch {
    // 隐私模式下 localStorage 会抛异常；主题偏好丢失可以接受，白屏不可以
    return 'light';
  }
}

export function AppShell() {
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [theme, setTheme] = useState<Theme>(readStoredTheme);

  useEffect(() => {
    document.documentElement.dataset['theme'] = theme;
    try {
      globalThis.localStorage?.setItem(THEME_KEY, theme);
    } catch {
      /* 同上：写入失败不影响本次会话的主题 */
    }
  }, [theme]);

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setDrawerOpen(false);
    };
    globalThis.addEventListener('keydown', onKeyDown);
    return () => globalThis.removeEventListener('keydown', onKeyDown);
  }, [drawerOpen]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  }, []);

  const active = findNavItem(location.pathname);
  const crumbs = active === undefined ? ['LicenseHarbor'] : ['LicenseHarbor', active.label];

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">
        跳到主内容
      </a>

      <Sidebar
        open={drawerOpen}
        onNavigate={() => setDrawerOpen(false)}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      {drawerOpen ? (
        <button
          type="button"
          className="drawer-scrim"
          aria-label="关闭导航菜单"
          onClick={() => setDrawerOpen(false)}
        />
      ) : null}

      <div className="main">
        <Topbar
          crumbs={crumbs}
          onOpenMenu={() => setDrawerOpen(true)}
          onOpenSearch={() => {
            globalThis.alert('全局搜索（Cmd+K）将在后续迭代实现。');
          }}
        />

        <main className="content" id="main-content" tabIndex={-1}>
          <div className="width-notice" role="status">
            <span className="width-notice__icon">
              <Icon name="triangle-alert" size={ICON_SIZE.inline} />
            </span>
            <span>
              LicenseHarbor 后台为桌面端设计，建议在 1280px 以上宽度使用。当前宽度下表格保持横向滚动，
              暂停 / 撤销等应急操作仍然可用。
            </span>
          </div>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
